import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type { UserResponse, LoginResponse } from '@@/types/api';
import { useAuthApi } from '~/composables/api/use-auth-api';
import {
  setUnauthorizedHandler,
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from '~/composables/api/use-api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserResponse | null>(null);
  const isLoading = ref(false);
  const isAuthenticated = computed(() => user.value !== null);
  const { trackEvent: matomoTrackEvent } = useMatomo();

  function setSentryUser(u: UserResponse) {
    if (import.meta.client) {
      Sentry.setUser({ id: String(u.id), email: u.email });
    }
  }

  function clearSentryUser() {
    if (import.meta.client) {
      Sentry.setUser(null);
    }
  }

  // Suppress the global unauthorized handler during expected 401s (e.g. session probe on app init)
  let _suppressUnauthorized = false;

  setUnauthorizedHandler(() => {
    if (_suppressUnauthorized) return;
    user.value = null;
    clearAccessToken();
    navigateTo('/login');
  });

  const authApi = useAuthApi();

  async function fetchUser() {
    if (!getAccessToken()) {
      user.value = null;
      return;
    }

    isLoading.value = true;
    _suppressUnauthorized = true;
    try {
      const response = await authApi.fetchCurrentUser();
      user.value = response;
      setSentryUser(response);
    } catch {
      user.value = null;
      clearSentryUser();
    } finally {
      _suppressUnauthorized = false;
      isLoading.value = false;
    }
  }

  async function completeAuth(response: LoginResponse) {
    setAccessToken(response.access_token);
    const profile = await authApi.fetchCurrentUser();
    user.value = profile;
    setSentryUser(profile);
  }

  async function login(email: string, password: string) {
    const response = await authApi.login({ email, password });
    await completeAuth(response);
  }

  async function exchangeCode(code: string) {
    const response = await authApi.exchangeCode({ code });
    await completeAuth(response);
  }

  function logout() {
    user.value = null;
    clearSentryUser();
    clearAccessToken();
    matomoTrackEvent('Auth', 'Logout');
  }

  return {
    user,
    isLoading,
    isAuthenticated,
    fetchUser,
    login,
    exchangeCode,
    logout,
  };
});
